import type { ComponentType } from 'react'
import type { OptionsDocument } from 'paintscout'
import {
  ButtonIcon,
  DividerIcon,
  ImageSmallIcon,
  QuoteIcon,
  SpacerIcon,
  TwoColIcon,
  ThreeColIcon
} from '@paintscout/ui'
import type { SvgIconProps } from '@paintscout/ui'
import { getObjectLabels, getPresentationBlockOptions } from '@paintscout/util/builder'
import type { LabelsOption } from '@paintscout/util/builder'
import TextFieldsOutlinedIcon from '@material-ui/icons/TextFieldsOutlined'
import CropPortraitIcon from '@material-ui/icons/CropPortrait'
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined'
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined'
import PhotoLibraryOutlinedIcon from '@material-ui/icons/PhotoLibraryOutlined'
import PhotoAlbumOutlinedIcon from '@material-ui/icons/PhotoAlbumOutlined'
import PhotoCameraOutlinedIcon from '@material-ui/icons/PhotoCameraOutlined'
import FormatQuoteIcon from '@material-ui/icons/FormatQuoteOutlined'
import MenuIcon from '@material-ui/icons/Menu'
import ShareIcon from '@material-ui/icons/Share'
import CodeIcon from '@material-ui/icons/Code'
import CompareIcon from '@material-ui/icons/Compare'

/** An icon for a group header — either a component (MUI / @paintscout/ui) or a Font Awesome class string, rendered as `<i className={icon} />`. */
export type BlockCatalogIcon = ComponentType<SvgIconProps> | string

/** An addable block type, resolved for display in an Add-block menu. */
export interface PresentationBlockCatalogTile {
  type: string
  label: string
  icon: ComponentType<SvgIconProps>
}

/** A labelled group of tiles ("Layout blocks", "Content blocks", "{quote label} blocks"). */
export interface PresentationBlockCatalogGroup {
  key: string
  label: string
  icon: BlockCatalogIcon
  tiles: PresentationBlockCatalogTile[]
}

const ESTIMATE_LABEL_PLACEHOLDER = '{estimateLabel}'

/**
 * THE list of block types offered by both Add-block menus — the inline "+" popup
 * (`AddBlockButton.tsx`) and the sidebar Add panel (`AddPanel.tsx`). It is the single source of
 * order, grouping and icon; change it here and both menus follow. Labels are not stored here, they
 * resolve from the v2 block catalog (see `getAddableBlockGroups`).
 *
 * Group membership + icons come from the design mock (`Presentation Builder V2.dc.html`
 * L361-478). `social-links`, `custom`, `slider-image`, and `pictures` are not in the mock's groups,
 * so they were added under the group that fits them.
 *
 * awards, mission, services, and documents are deliberately absent (#23946): they are deprecated
 * in favor of composing text/media/gallery/2-column blocks, and their renderers only exist to draw
 * existing documents.
 */
const BLOCK_GROUPS: Array<{
  key: string
  label: string
  icon: BlockCatalogIcon
  tiles: Array<{ type: string; icon: ComponentType<SvgIconProps> }>
}> = [
  {
    key: 'content',
    label: 'Content blocks',
    icon: PhotoAlbumOutlinedIcon,
    tiles: [
      { type: '2-column', icon: TwoColIcon },
      { type: '3-column', icon: ThreeColIcon },
      { type: 'cards', icon: CropPortraitIcon },
      { type: 'logo', icon: AccountCircleOutlinedIcon },
      { type: 'banner', icon: PhotoAlbumOutlinedIcon },
      { type: 'text', icon: TextFieldsOutlinedIcon },
      { type: 'image', icon: ImageSmallIcon },
      { type: 'gallery', icon: PhotoLibraryOutlinedIcon },
      { type: 'button', icon: ButtonIcon },
      { type: 'testimonials', icon: FormatQuoteIcon },
      { type: 'social-links', icon: ShareIcon },
      { type: 'slider-image', icon: CompareIcon },
      { type: 'custom', icon: CodeIcon }
    ]
  },
  {
    key: 'estimate',
    label: `${ESTIMATE_LABEL_PLACEHOLDER} blocks`,
    icon: 'fa-regular fa-clipboard-list',
    tiles: [
      { type: 'quote', icon: QuoteIcon },
      // the quote's own line-item photos (ENG-1402) — `gallery` already owns PhotoLibraryOutlined
      { type: 'pictures', icon: PhotoCameraOutlinedIcon },
      { type: 'terms', icon: MenuIcon },
      { type: 'about-estimator', icon: AccountCircleOutlinedIcon },
      { type: 'work-order', icon: AssignmentOutlinedIcon }
    ]
  },
  {
    key: 'layout',
    label: 'Layout blocks',
    icon: MenuIcon,
    tiles: [
      { type: 'divider', icon: DividerIcon },
      { type: 'spacer', icon: SpacerIcon }
    ]
  }
]

/**
 * Resolves the addable block groups for an Add-block menu. Labels come live from the v2 block
 * catalog, so a catalog rename follows through to both menus. Pass `objectLabels` when the caller
 * has an invoice-aware set (the inline menu does); otherwise they derive from `options`.
 *
 * The catalog can list a type twice (`text` has a v2 "Text" entry and a shared "Text Block" one).
 * The first entry wins, the same one `handleAddBlock` builds the block from, so the label always
 * names the block that gets created.
 */
export function getAddableBlockGroups(args: {
  options?: OptionsDocument
  objectLabels?: LabelsOption
}): PresentationBlockCatalogGroup[] {
  const { options } = args
  const objectLabels = args.objectLabels ?? getObjectLabels({ options })
  const estimateLabel = objectLabels.quote.value

  const catalogLabels = new Map<string, string>()
  for (const option of getPresentationBlockOptions({ options })) {
    if (!catalogLabels.has(option.type)) catalogLabels.set(option.type, option.label)
  }

  // the catalog builds these from `options` alone, so an invoice's own labels are applied here
  const labelOverrides: Record<string, string> = {
    quote: estimateLabel,
    pictures: `${estimateLabel} Pictures`,
    'work-order': objectLabels.workOrder.value
  }

  return BLOCK_GROUPS.map((group) => ({
    key: group.key,
    label: group.label.replace(ESTIMATE_LABEL_PLACEHOLDER, estimateLabel),
    icon: group.icon,
    tiles: group.tiles.map(({ type, icon }) => ({
      type,
      label: labelOverrides[type] ?? catalogLabels.get(type) ?? type,
      icon
    }))
  })).filter((group) => group.tiles.length > 0)
}
